import { Box, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useMotionConfig } from "../motion/useMotionConfig";

const MotionBox = motion(Box);

/**
 * Animated multi-layer "aurora" gradient background with a faint grid.
 * Used behind hero and CTA sections to create depth and a tech feel.
 * The blobs drift slowly; motion is disabled for reduced-motion users.
 */
export default function AuroraBackground({ variant = "default" }) {
  const theme = useTheme();
  const { disabled } = useMotionConfig();
  const isDark = theme.palette.mode === "dark";

  const gridColor = isDark ? "rgba(148,163,184,0.08)" : "rgba(11,18,32,0.05)";

  const blobs = [
    { color: "#4f7cff", size: 520, top: "-12%", left: "-8%", dur: 18 },
    { color: "#a855f7", size: 460, top: "10%", right: "-6%", dur: 22 },
    { color: "#22d3ee", size: 420, bottom: "-15%", left: "25%", dur: 26 },
  ];

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        zIndex: 0,
        pointerEvents: "none",
        bgcolor: variant === "solid" ? "background.default" : "transparent",
      }}
    >
      {/* Grid overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(${gridColor} 1px, transparent 1px), linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
        }}
      />

      {/* Gradient blobs */}
      {blobs.map((b, i) => {
        const common = {
          position: "absolute",
          width: b.size,
          height: b.size,
          top: b.top,
          left: b.left,
          right: b.right,
          bottom: b.bottom,
          borderRadius: "50%",
          background: b.color,
          filter: "blur(90px)",
          opacity: isDark ? 0.5 : 0.35,
        };

        if (disabled) {
          return <Box key={i} sx={common} />;
        }

        return (
          <MotionBox
            key={i}
            sx={common}
            animate={{
              x: [0, 40, -30, 0],
              y: [0, -30, 40, 0],
              scale: [1, 1.12, 0.95, 1],
            }}
            transition={{ duration: b.dur, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      })}
    </Box>
  );
}
